"use client";
import React, { useEffect, useMemo, useCallback } from "react";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import Tag from "@/components/Tags/Tag";
import type { IconTypes } from "@/components/icons/types";
import CareerStatComponent from "@/components/CareerCards/CareerStat";
import CareerKeySkills from "@/components/CareerCards/CareerKeySkills";
import useWebview from "@/webview/useWebview";
import type { EnrichedProgram } from "@/common/course/types";
import IndustryHighlightsCardsSlider from "./IndustryHighlightsCardsSlider";
import { convertHitsToEnrichedPrograms } from "./utils/convertHitsToEnrichedPrograms";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import { useInView } from "react-intersection-observer";
import { getPageType } from "@/utils/tagular/helpers";
import { getTopSkills } from "./utils/getTopSkills";
import { useInstantSearch } from "react-instantsearch";
import { useTypedSearchHits } from "../Search/hooks/useTypedSearchHits";

export interface IndustryHighlightsContentProps {
  tagText?: string;
  tagIcon?: IconTypes;
  title?: string;
  subject?: string;
  tasksTitle?: string;
  tasks?: string[];
  stats?: Array<{
    label: string;
    value: string;
    icon: IconTypes;
  }>;
  skills?: string[];
  courses?: (EnrichedProgram & {
    _badges?: Array<{ label: string; icon?: IconTypes }>;
    _level?: "Introductory" | "Intermediate" | "Advanced";
  })[];
  cta?: {
    ctaHref: string;
    showSearchResultsNumber: boolean;
    ctaTextBeforeNumber: string;
    ctaTextAfterNumber: string;
  };
  disclaimerText?: string;
  disclaimerLinkText?: string;
  disclaimerTextAfter?: string;
  disclaimerLink?: string;
  numberOfHits?: number;
  filterParams?: string;
}

export function IndustryHighlightsContent({
  tagText,
  tagIcon,
  title,
  subject,
  tasksTitle,
  tasks,
  stats,
  skills,
  courses,
  cta = {
    ctaHref: "/search",
    showSearchResultsNumber: true,
    ctaTextBeforeNumber: "Browse",
    ctaTextAfterNumber: "related courses",
  },
  disclaimerText = "This information is US-specific and can be found at the",
  disclaimerLinkText = "Bureau of Labor Statistics",
  disclaimerLink = "https://www.bls.gov/",
  disclaimerTextAfter = "as of 2025",
  numberOfHits = 25,
  filterParams,
}: IndustryHighlightsContentProps) {
  const { getUrl } = useWebview();
  const { items: hits } = useTypedSearchHits();
  const { scopedResults } = useInstantSearch();
  const totalHits = scopedResults[0]?.results?._rawResults[0]?.nbHits || 0;
  const displayHits = hits?.slice(0, numberOfHits);
  const allHitsSkills: string[] =
    hits
      ?.map((hit) => hit.hoverOverlay?.skillsList)
      .flat()
      .filter((skill): skill is string => skill !== undefined) || [];
  const topSkills = getTopSkills(allHitsSkills);
  // Create search URL with subject and filtered by courses
  const getSearchUrl = useCallback((): string => {
    const params = new URLSearchParams(filterParams);
    return `/search?${params.toString()}`;
  }, [filterParams]);

  // Calculate CTA text and href
  const displayCtaText = useMemo(() => {
    if (!cta) return "";

    if (cta.showSearchResultsNumber && totalHits > 0) {
      return `${cta.ctaTextBeforeNumber} ${totalHits} ${cta.ctaTextAfterNumber}`;
    }

    return `${cta.ctaTextBeforeNumber} ${cta.ctaTextAfterNumber}`;
  }, [cta, totalHits]);

  const displayCtaHref = useMemo(() => {
    return displayHits?.length > 0 ? getSearchUrl() : cta?.ctaHref || "/search";
  }, [displayHits?.length, subject, getSearchUrl, cta?.ctaHref]);

  const enrichedPrograms = convertHitsToEnrichedPrograms(displayHits);
  const displayCourses =
    enrichedPrograms && enrichedPrograms.length > 0
      ? enrichedPrograms
      : courses;
  const pageType = useMemo(() => getPageType(), []);

  const normalizePageType = useCallback(
    (
      type: string | undefined,
      defaultType: "Learn Page" | "learn-page" = "Learn Page",
    ) => {
      return type === "other" ? defaultType : type;
    },
    [],
  );

  const pageContext = useMemo(() => {
    if (typeof window === "undefined") return null;
    const normalizedPageType =
      normalizePageType(pageType, "Learn Page") || "Learn Page";
    return {
      title: document.title,
      url: window.location.href,
      pageType: normalizedPageType,
    };
  }, [pageType, normalizePageType]);

  const location = useMemo(() => {
    return normalizePageType(pageType, "learn-page");
  }, [pageType, normalizePageType]);

  const webContext = useMemo(() => {
    return pageContext
      ? {
          page: {
            title: pageContext.title,
            url: pageContext.url,
            pageType: pageContext.pageType,
          },
        }
      : undefined;
  }, [pageContext]);

  const CTA_CUSTOM_DIMENSIONS = useMemo(
    () => [
      {
        key: "component",
        value: "Industry Highlights",
      },
    ],
    [],
  );

  const { elementViewed } = useElementEvents({
    webElement: {
      elementType: "BUTTON",
      htmlId: "browse-related-courses",
      location: "industry-highlights-asset",
      name: "browse-related-courses",
      text: displayCtaText || "",
    },
    webContext,
  });

  const { elementClicked } = useElementEvents({
    webElement: {
      elementType: "BUTTON",
      name: "browse-related-courses",
      text: displayCtaText || "",
      location: location,
    },
    customDimensions: CTA_CUSTOM_DIMENSIONS,
  });

  const { ref: buttonRef, inView } = useInView({
    threshold: 0.5,
    triggerOnce: true,
  });

  useEffect(() => {
    if (inView && displayCtaText) {
      elementViewed();
    }
  }, [inView, displayCtaText, elementViewed]);

  // Don't render if no essential data is provided
  if (!title && !tagText && !tasks && !stats) {
    return null;
  }

  const finalTasksArray = tasks || [];
  const finalStatsArray = stats || [];
  const finalSkillsArray = topSkills || skills || [];

  return (
    <div className="xl:max-w-[1331px] mx-auto w-full px-4 xl:px-0 py-12">
      <style>{`
        @media (min-width: 1279px) {
          .industry-highlights-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
        }
      `}</style>
      <div className="bg-secondary-100 rounded-2xl border border-putty-400 p-5 md:p-8 lg:p-10">
        {/* Top Section - Full Width: Tag and Title */}
        <div className="flex flex-col md:flex-row items-start md:items-center gap-4 mb-8">
          {tagText && (
            <Tag
              title={tagText}
              color="fern"
              borderless={true}
              icon={tagIcon}
              className="px-2 py-1 text-xs font-bold [border-radius:4px]"
            />
          )}
          {title && (
            <h2 className="text-2xl md:text-3xl font-bold text-primary mb-0 mt-0">
              {title}
            </h2>
          )}
        </div>

        {/* Middle Section - Two Columns */}
        <div className="grid grid-cols-1 gap-8 mb-6 industry-highlights-grid">
          {/* Left Column - Career Information */}
          <div className="flex flex-col gap-8">
            {finalTasksArray.length > 0 && (
              <div>
                <p className="text-lg font-semibold text-primary mb-3">
                  {tasksTitle}
                </p>
                <ul className="list-disc list-outside space-y-1 text-base text-gray-800 pl-5 my-0">
                  {finalTasksArray.map((task, index) => (
                    <li key={index}>{task}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Career Stats */}
            {finalStatsArray.length > 0 && (
              <div className="flex flex-col gap-6">
                {finalStatsArray.map((stat, index) => (
                  <CareerStatComponent
                    key={index}
                    label={stat.label}
                    value={stat.value}
                    icon={stat.icon as IconTypes}
                    singleLine={true}
                    className="items-center"
                    showIconOnMobile={true}
                  />
                ))}
              </div>
            )}

            {/* Career Key Skills */}
            {finalSkillsArray.length > 0 && (
              <div className="hidden md:block">
                <CareerKeySkills
                  skills={finalSkillsArray}
                  singleLine={false}
                  titleClassName="text-primary"
                />
              </div>
            )}
          </div>

          {/* Right Column - Course Recommendations */}
          <div className="flex flex-col w-full">
            {displayCourses && displayCourses.length > 0 ? (
              <IndustryHighlightsCardsSlider
                programs={enrichedPrograms || []}
                getUrl={getUrl}
              />
            ) : null}
          </div>
        </div>

        {/* Bottom Section - Full Width: Gray Line, Disclaimer and CTA */}
        <div className="border-t border-putty-400 md:border-secondary-200 pt-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <p className="text-xs text-gray-600 mb-0">
              {disclaimerText}{" "}
              <a
                href={disclaimerLink}
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-primary"
              >
                {disclaimerLinkText}
              </a>{" "}
              {disclaimerTextAfter}.
            </p>

            {displayCtaText && displayCtaHref && (
              <div ref={buttonRef} className="self-center">
                <Button
                  asChild
                  variant="brand"
                  size="lg"
                  className="w-auto h-10 bg-brand hover:bg-brand-hover border-brand"
                  id="browse-related-courses"
                  onClick={() => {
                    elementClicked({
                      webElement: {
                        text: displayCtaText || "",
                      },
                      outboundUrl: displayCtaHref,
                    });
                  }}
                >
                  <Link
                    href={displayCtaHref}
                    className="no-underline hover:no-underline"
                  >
                    {displayCtaText}
                  </Link>
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
